/* eslint-disable @nx/enforce-module-boundaries -- deep `libs/xplat/api/dto` paths avoid the dto barrel -> tool-tracker -> xplat-api-services cycle */
import type { ForemanJobDetail } from '../../../../dto/src/lib/models/foreman/foreman-job-detail.model';
import type { ForemanTimeSheet } from '../../../../dto/src/lib/models/foreman/foreman-time-sheet.model';
import moment from 'moment';

export interface ShiftExceptionsContext {
  selectedDate: Date | null;
  selectedShift: number | null;
  selectedForemanJobCrewID: string | null;
}

function normalizeShift(value: number | null | undefined): number | null {
  if (value == null || Number(value) === 0) {
    return null;
  }
  return Number(value);
}

export function matchesShiftExceptionsContext(
  timeSheet: ForemanTimeSheet,
  context: ShiftExceptionsContext
): boolean {
  if (!context.selectedDate) {
    return false;
  }

  const selectedDateKey = moment(context.selectedDate).format('MM/DD/YYYY');
  const workDateKey = moment(timeSheet.workDate).format('MM/DD/YYYY');
  if (workDateKey !== selectedDateKey) {
    return false;
  }

  const selectedShift = normalizeShift(context.selectedShift);
  const sheetShift = normalizeShift(timeSheet.shift);
  if (selectedShift !== sheetShift) {
    return false;
  }

  const crewId = String(context.selectedForemanJobCrewID ?? '').trim();
  const sheetCrewId = String(timeSheet.foremanJobCrewID ?? '').trim();
  return crewId === sheetCrewId;
}

export function findTimeSheetForShiftExceptions(
  jobDetail: ForemanJobDetail | null | undefined,
  context: ShiftExceptionsContext
): ForemanTimeSheet | undefined {
  return (jobDetail?.timeSheets ?? []).find((timeSheet) =>
    matchesShiftExceptionsContext(timeSheet, context)
  );
}

export function resolveShiftExceptionsFromJobDetail(
  jobDetail: ForemanJobDetail | null | undefined,
  context: ShiftExceptionsContext
): string {
  const timeSheet = findTimeSheetForShiftExceptions(jobDetail, context);
  return timeSheet?.exceptions?.trim() ?? jobDetail?.exceptions?.trim() ?? '';
}

export function applyShiftExceptionsToJobDetail(
  jobDetail: ForemanJobDetail,
  exceptionsText: string | null | undefined,
  context: ShiftExceptionsContext
): ForemanJobDetail {
  const normalized = String(exceptionsText ?? '').trim();
  const exceptions = normalized.length > 0 ? normalized : null;
  const timeSheets = [...(jobDetail.timeSheets ?? [])];
  const existingIndex = timeSheets.findIndex((timeSheet) =>
    matchesShiftExceptionsContext(timeSheet, context)
  );

  if (existingIndex >= 0) {
    timeSheets[existingIndex] = {
      ...timeSheets[existingIndex],
      exceptions,
    };
  }

  return {
    ...jobDetail,
    timeSheets,
    exceptions,
  };
}

export function resolveShiftExceptionsForSubmit(
  jobDetail: ForemanJobDetail | null | undefined,
  context: ShiftExceptionsContext
): string | null {
  const value = resolveShiftExceptionsFromJobDetail(jobDetail, context);
  return value.length > 0 ? value : null;
}
